/**
 * Arcade-sim vehicle physics.
 *
 * The model is a velocity-based bicycle model with per-axle slip angles and
 * load-sensitive friction:
 *
 *   1. steering angle is rate-limited and shrinks with speed,
 *   2. the surface under each of the four wheels is sampled separately,
 *   3. longitudinal forces (engine, brake, drag, rolling resistance) act along
 *      the car's heading,
 *   4. lateral tyre forces come from slip angles, clamped by mu * Fz where the
 *      axle load includes dynamic weight transfer,
 *   5. the resulting body-frame accelerations are integrated with the
 *      centripetal coupling terms, which is what makes the car slide when the
 *      tyres saturate instead of simply following its nose.
 *
 * Because the drive force and mu are tracked per axle, a car straddling a kerb
 * or a wet patch genuinely behaves differently on each side.
 */

import { clamp, wrapAngle } from './math.js';
import { CAR_GRAVITY, POWER_AT_TOP, type CarHandling } from './cars.js';
import { SURFACES, SURFACE_LIST, type SurfaceId } from './surfaces.js';
import { cellNearSolid, GRID_CELL, isRoadAt, isSolidAt, type BuiltTrack } from './track.js';

export interface VehicleInput {
  /** 0..1 */
  throttle: number;
  /** 0..1 (also drives reverse when stationary) */
  brake: number;
  /** -1 (right) .. 1 (left) */
  steer: number;
  handbrake: boolean;
  boost: boolean;
}

export interface VehicleState {
  x: number;
  y: number;
  angle: number;
  vx: number;
  vy: number;
  yawRate: number;
  /** Current front wheel angle in radians. */
  steerAngle: number;
  /** Local body-frame velocities, refreshed every step. */
  vLong: number;
  vLat: number;
  speed: number;
  gear: number;
  rpm: number;
  /** Seconds left in the current gear-change torque cut. */
  shiftTimer: number;
  /** Remaining turbo seconds. */
  turbo: number;
  /** Turbo recharge progress 0..1. */
  turboCharge: number;
  /** 0..1 handbrake/slide intensity, drives smoke particles. */
  slip: number;
  /** 0..1 wheelspin intensity, drives tyre smoke and squeal. */
  wheelSpin: number;
  surfaceFront: SurfaceId;
  surfaceRear: SurfaceId;
  /** Set to the impact speed (px/s) for one frame after a wall/car hit. */
  impact: number;
  /** Impact normal, used to pick a spark direction. */
  impactNx: number;
  impactNy: number;
  onRoad: boolean;
  /** Distance driven, used for stats. */
  odometer: number;
  /** Seconds since the last collision, for respawn assist. */
  stuckTime: number;
  /** Total time spent not moving while throttle is applied. */
  reversing: boolean;
}

export function createVehicleState(x: number, y: number, angle: number): VehicleState {
  return {
    x, y, angle, vx: 0, vy: 0, yawRate: 0, steerAngle: 0,
    vLong: 0, vLat: 0, speed: 0, gear: 1, rpm: 900, shiftTimer: 0,
    turbo: 0, turboCharge: 1, slip: 0, wheelSpin: 0,
    surfaceFront: 'asphalt', surfaceRear: 'asphalt',
    impact: 0, impactNx: 0, impactNy: 0,
    onRoad: true, odometer: 0, stuckTime: 0, reversing: false,
  };
}

export const NEUTRAL_INPUT: VehicleInput = { throttle: 0, brake: 0, steer: 0, handbrake: false, boost: false };

export function makeInput(): VehicleInput {
  return { throttle: 0, brake: 0, steer: 0, handbrake: false, boost: false };
}

const GEARS = 6;

const SIM = {
  /** Velocity floor used in the slip-angle denominator to stay stable at rest. */
  slipVelFloor: 14,
  /** Extra yaw damping so the car does not oscillate when grip is high. */
  yawDamping: 1.35,
  /** Restitution against walls. */
  wallRestitution: 0.28,
  /** How much of a wall impact becomes a yaw impulse. */
  wallYawKick: 0.0016,
  /** Speed scrub factor on wall contact. */
  wallSpeedScrub: 0.55,
  /** Boost pad force multiplier relative to engine force. */
  boostPadForce: 0.55,
  /** Turbo recharge rate (turbo-seconds per second). */
  turboRecharge: 0.42,
  /** How quickly the tyre load recovers after wheelspin. */
  spinRecovery: 3.2,
  /** Low-speed yaw authority so parked cars can still pivot a little. */
  parkingYaw: 0.35,
  /** Tyres brake a little harder than they corner thanks to load transfer. */
  longitudinalGrip: 1.15,
};

/** Grip multiplier from a surface, including car-specific bonuses. */
export function surfaceGrip(handling: CarHandling, s: SurfaceId): number {
  const def = SURFACES[s] ?? SURFACES.asphalt;
  const bonus = handling.gripBonus[s] ?? 0;
  return def.grip + bonus;
}

export interface StepResult {
  /** Collision with a wall happened during this step. */
  wallHit: boolean;
  /** Impact speed in px/s. */
  impact: number;
}

/**
 * Advances one vehicle by `dt` seconds. Mutates `state` in place (no
 * allocations), which keeps the fixed-timestep loop and the server tick cheap.
 */
export function stepVehicle(
  state: VehicleState,
  handling: CarHandling,
  input: VehicleInput,
  track: BuiltTrack,
  dt: number,
): StepResult {
  state.impact = 0;
  state.impactNx = 0;
  state.impactNy = 0;

  // ---------------------------------------------------------------- steering
  const speed = Math.hypot(state.vx, state.vy);
  const speedFrac = clamp(speed / handling.maxSpeed, 0, 1);
  const steerLimit = handling.steerMax * (1 - handling.steerFalloff * speedFrac * speedFrac);
  const targetSteer = clamp(input.steer, -1, 1) * steerLimit;
  const rate = (Math.abs(input.steer) > 0.01 ? handling.steerRate : handling.steerReturn) * steerLimit;
  state.steerAngle += clamp(targetSteer - state.steerAngle, -rate * dt, rate * dt);

  // ---------------------------------------------------- local velocity frame
  const cs = Math.cos(state.angle);
  const sn = Math.sin(state.angle);
  let vLong = state.vx * cs + state.vy * sn;
  let vLat = -state.vx * sn + state.vy * cs;

  // ------------------------------------------------------ surface sampling
  // Sample each wheel individually, then average per axle.
  const halfLong = handling.wheelBase / 2;
  const halfLat = handling.width * 0.42;
  const fx = state.x + cs * halfLong;
  const fy = state.y + sn * halfLong;
  const rx = state.x - cs * halfLong;
  const ry = state.y - sn * halfLong;
  const sFL = sampleSurface(track, fx - sn * halfLat, fy + cs * halfLat);
  const sFR = sampleSurface(track, fx + sn * halfLat, fy - cs * halfLat);
  const sRL = sampleSurface(track, rx - sn * halfLat, ry + cs * halfLat);
  const sRR = sampleSurface(track, rx + sn * halfLat, ry - cs * halfLat);
  state.surfaceFront = sFL;
  state.surfaceRear = sRL;
  state.onRoad = isOnRoad(track, fx, fy) && isOnRoad(track, rx, ry);

  const gripFL = surfaceGrip(handling, sFL);
  const gripFR = surfaceGrip(handling, sFR);
  const gripRL = surfaceGrip(handling, sRL);
  const gripRR = surfaceGrip(handling, sRR);
  const gripFront = (gripFL + gripFR) / 2;
  const gripRear = (gripRL + gripRR) / 2;
  const accelFront = (SURFACES[sFL].accel + SURFACES[sFR].accel) / 2;
  const accelRear = (SURFACES[sRL].accel + SURFACES[sRR].accel) / 2;
  const rollFront = (SURFACES[sFL].rollResist + SURFACES[sFR].rollResist) / 2;
  const rollRear = (SURFACES[sRL].rollResist + SURFACES[sRR].rollResist) / 2;
  const dragSurface = (SURFACES[sRL].drag + SURFACES[sRR].drag) / 2;
  const topSpeedSurface = (SURFACES[sRL].topSpeed + SURFACES[sRR].topSpeed) / 2;

  // ------------------------------------------------------------ engine force
  const gears = GEARS;
  const gearSpan = Math.max(40, handling.maxSpeed / gears);
  // Hysteresis is essential: without it a car settling exactly on a gear
  // boundary upshifts and downshifts forever and the torque cut pins it there.
  let gear = state.gear;
  if (gear < gears && vLong >= gearSpan * gear + 2) gear++;
  else if (gear > 1 && vLong < gearSpan * (gear - 1) - 6) gear--;
  const rpm = clamp(900 + (clamp(vLong - (gear - 1) * gearSpan, 0, gearSpan) / gearSpan) * 6600, 900, 7600);
  // The torque cut is a timed event rather than a speed window, so a car can
  // always drive through it.
  if (gear > state.gear) state.shiftTimer = handling.gearShiftTime;
  state.shiftTimer = Math.max(0, state.shiftTimer - dt);
  const shiftCut = state.shiftTimer > 0 ? 0.45 : 1;

  let driveWheelAccel: number;
  let driveWheelGrip: number;
  if (handling.drive === 'fwd') {
    driveWheelAccel = accelFront;
    driveWheelGrip = gripFront;
  } else if (handling.drive === 'awd') {
    driveWheelAccel = accelFront * handling.driveBias + accelRear * (1 - handling.driveBias);
    driveWheelGrip = gripFront * handling.driveBias + gripRear * (1 - handling.driveBias);
  } else {
    driveWheelAccel = accelRear;
    driveWheelGrip = gripRear;
  }

  const boostPad = Math.max(SURFACES[sFL].boost ?? 0, SURFACES[sRL].boost ?? 0);
  const boostPadForce = boostPad > 0 && vLong > -5 ? handling.engineForce * SIM.boostPadForce * boostPad : 0;

  // Turbo / nitrous with recharge.
  let turboForce = 0;
  const wantBoost = input.boost && state.turboCharge > 0.05 && input.throttle > 0.1;
  if (wantBoost) {
    turboForce = handling.turboForce;
    state.turboCharge = Math.max(0, state.turboCharge - dt / Math.max(0.4, handling.turboTime));
    state.turbo = state.turboCharge * handling.turboTime;
  } else {
    state.turboCharge = Math.min(1, state.turboCharge + dt * SIM.turboRecharge);
    state.turbo = state.turboCharge * handling.turboTime;
  }

  const engineMax = handling.engineForce * driveWheelAccel * shiftCut;
  const forwardSpeedFrac = clamp(Math.max(0, vLong) / (handling.maxSpeed * topSpeedSurface), 0, 1);
  // Torque fades toward the limiter; at max speed exactly POWER_AT_TOP of the
  // peak force remains, which is what the derived drag coefficient balances.
  const powerCurve = 1 - (1 - POWER_AT_TOP) * forwardSpeedFrac * forwardSpeedFrac;
  let driveForce = input.throttle * engineMax * powerCurve + boostPadForce + turboForce;

  // Reverse gear: brake input at (near) standstill drives backwards.
  let brakeForce = 0;
  state.reversing = false;
  if (vLong <= 4 && input.brake > 0.05 && input.throttle < 0.05) {
    state.reversing = true;
    driveForce = -input.brake * handling.engineForce * driveWheelAccel * 0.45;
  } else {
    brakeForce = input.brake * handling.brakeForce;
  }
  if (vLong > 0 && driveForce < 0) driveForce = 0;

  const dragForce = handling.drag * dragSurface * vLong * Math.abs(vLong);
  const rollSurface = (rollFront + rollRear) / 2;
  const rollForce = Math.sign(vLong) * handling.rollResist * handling.mass * (rollSurface / 1.2);

  const a = handling.wheelBase * (1 - handling.frontWeight);
  const b = handling.wheelBase * handling.frontWeight;
  const speedNorm = clamp(speed / handling.maxSpeed, 0, 1);
  const downforceMu = handling.downforce * speedNorm * speedNorm;
  const muFront = (handling.muBase + downforceMu) * gripFront;
  const muRearRaw = (handling.muBase + downforceMu) * gripRear;
  const muRear = muRearRaw * (input.handbrake ? handling.handbrakeMu : 1);

  // Effective "longitudinal" force used for weight transfer estimation.
  let fxTotal = driveForce - dragForce - rollForce;
  if (state.reversing) fxTotal = driveForce;
  if (brakeForce > 0 && Math.abs(vLong) > 0.5) fxTotal -= Math.sign(vLong) * brakeForce;
  if (input.handbrake && Math.abs(vLong) > 4) {
    // Handbrake locks the rear axle: the wheel stops rolling and drags.
    fxTotal -= Math.sign(vLong) * handling.brakeForce * 0.35;
  }

  // Tyres can only generate so much longitudinal force. Without this clamp the
  // brakes would work identically on asphalt and on ice, which is the single
  // most noticeable way a surface model can feel fake.
  const staticFront = (handling.mass * CAR_GRAVITY * b) / handling.wheelBase;
  const staticRear = (handling.mass * CAR_GRAVITY * a) / handling.wheelBase;
  const maxLongForce = (muFront * staticFront + muRear * staticRear) * SIM.longitudinalGrip;
  fxTotal = clamp(fxTotal, -maxLongForce, maxLongForce);

  // ---------------------------------------------------------- lateral forces
  const safeV = Math.max(Math.abs(vLong), SIM.slipVelFloor) * Math.sign(vLong || 1);
  const slipFront = Math.atan2(vLat + a * state.yawRate, Math.abs(safeV)) - state.steerAngle;
  const slipRear = Math.atan2(vLat - b * state.yawRate, Math.abs(safeV));

  // Dynamic axle loads (longitudinal weight transfer).
  const aLongEst = fxTotal / handling.mass;
  const transfer = (handling.mass * aLongEst * handling.comHeight) / Math.max(8, handling.wheelBase);
  const fzFront = Math.max(0, staticFront - transfer);
  const fzRear = Math.max(0, staticRear + transfer * (handling.drive === 'fwd' ? 0.6 : 1));

  // Friction circle: whatever the tyres spend going forwards/backwards is no
  // longer available for cornering, so braking hard mid-corner makes the car
  // run wide exactly as it should.
  const longUse = clamp(Math.abs(fxTotal) / Math.max(1, maxLongForce), 0, 1);
  const circle = Math.sqrt(Math.max(0.15, 1 - longUse * longUse * 0.85));

  const maxFyFront = muFront * fzFront * circle;
  const maxFyRear = muRear * fzRear * circle;
  const fyFront = clamp(-handling.cornerStiffness * fzFront * slipFront, -maxFyFront, maxFyFront);
  const fyRear = clamp(-handling.cornerStiffness * fzRear * slipRear, -maxFyRear, maxFyRear);

  // ------------------------------------------------------------- integration
  const inertia = (handling.mass * handling.inertiaScale * (handling.length * handling.length + handling.width * handling.width)) / 12;
  const cosSteer = Math.cos(state.steerAngle);
  const sinSteer = Math.sin(state.steerAngle);

  const aLong = (fxTotal - fyFront * sinSteer) / handling.mass + vLat * state.yawRate;
  const aLat = (fyFront * cosSteer + fyRear) / handling.mass - vLong * state.yawRate;
  let yawAcc = (a * fyFront * cosSteer - b * fyRear) / Math.max(200, inertia);
  yawAcc -= state.yawRate * SIM.yawDamping;

  // Low-speed stability: without this a parked car can jitter from tiny forces.
  if (speed < 26) {
    const blend = speed / 26;
    yawAcc *= blend;
    vLat *= 1 - (1 - blend) * 0.6;
    if (Math.abs(vLong) < 3 && input.throttle < 0.02 && input.brake < 0.02) {
      vLong *= 1 - Math.min(1, dt * 6);
      vLat *= 1 - Math.min(1, dt * 6);
    }
  }

  vLong += aLong * dt;
  vLat += aLat * dt;
  state.yawRate += yawAcc * dt;
  if (input.handbrake && speed > 20) state.yawRate += Math.sign(state.steerAngle) * SIM.parkingYaw * dt * (speedNorm * 2 + 0.4);

  // Clamp forward/reverse speed to the limits (plus the loose-surface penalty).
  const vMax = handling.maxSpeed * topSpeedSurface;
  if (vLong > vMax) vLong = vMax;
  if (vLong < -handling.reverseMaxSpeed) vLong = -handling.reverseMaxSpeed;

  state.angle = wrapAngle(state.angle + state.yawRate * dt);
  if (!Number.isFinite(state.angle)) state.angle = 0;

  // Safety net. A car wedged into a corner can otherwise accumulate rotational
  // energy until the heading stops being a finite number, which would then
  // poison every downstream calculation (and the network snapshots).
  if (!Number.isFinite(state.yawRate)) state.yawRate = 0;
  if (!Number.isFinite(vLong)) vLong = 0;
  if (!Number.isFinite(vLat)) vLat = 0;
  state.yawRate = clamp(state.yawRate, -7.5, 7.5);
  vLong = clamp(vLong, -700, 900);
  vLat = clamp(vLat, -700, 700);

  const ncs = Math.cos(state.angle);
  const nsn = Math.sin(state.angle);
  let worldVx = ncs * vLong - nsn * vLat;
  let worldVy = nsn * vLong + ncs * vLat;

  const prevX = state.x;
  const prevY = state.y;
  let nx = state.x + worldVx * dt;
  let ny = state.y + worldVy * dt;
  const wantX = nx - prevX;
  const wantY = ny - prevY;

  // ------------------------------------------------------------ wall handling
  let result: StepResult = { wallHit: false, impact: 0 };
  const hit = collideWalls(track, nx, ny, state.angle, prevX, prevY, halfLong + 4, halfLat + 2);
  if (hit.hit) {
    nx = hit.x;
    ny = hit.y;
    const vn = worldVx * hit.nx + worldVy * hit.ny;
    const impact = Math.max(0, -vn);
    const wanted = Math.hypot(wantX, wantY);
    const pushed = Math.hypot(nx - prevX, ny - prevY);
    if (vn < 0) {
      worldVx -= (1 + SIM.wallRestitution) * vn * hit.nx;
      worldVy -= (1 + SIM.wallRestitution) * vn * hit.ny;
      // Scrub some tangential speed against the barrier.
      worldVx *= SIM.wallSpeedScrub + 0.45;
      worldVy *= SIM.wallSpeedScrub + 0.45;
      state.yawRate += (hit.side * SIM.wallYawKick * impact) / Math.max(1, Math.abs(state.yawRate) + 1);
      state.impact = impact;
      state.impactNx = hit.nx;
      state.impactNy = hit.ny;
      result = { wallHit: true, impact };
      state.stuckTime = 0;
    } else if (wanted > 0.4 && pushed < wanted * 0.25) {
      // The car asked to move but the barrier gave it almost nothing back: it is
      // wedged in a corner, not sliding along a wall. Bleed the velocity away so
      // it cannot grind in place at full speed for ever.
      worldVx *= 0.45;
      worldVy *= 0.45;
    }
  } else {
    state.stuckTime += dt;
  }

  state.x = nx;
  state.y = ny;
  state.vx = worldVx;
  state.vy = worldVy;

  // Refresh derived values.
  const newSpeed = Math.hypot(worldVx, worldVy);
  state.speed = newSpeed;
  state.vLong = worldVx * ncs + worldVy * nsn;
  state.vLat = -worldVx * nsn + worldVy * ncs;
  state.gear = gear;
  state.rpm = rpm;
  state.steerAngle = clamp(state.steerAngle, -1.2, 1.2);

  // Slide and wheelspin estimates drive particles, audio and drift scoring.
  const ang = Math.atan2(Math.abs(state.vLat), Math.max(6, Math.abs(state.vLong)));
  const latSlip = clamp((ang - 0.06) / 0.55, 0, 1);
  const driveLimit = (handling.drive === 'fwd' ? muFront * fzFront : maxFyRear) || 1;
  const spinTarget = clamp((Math.abs(driveForce) - driveLimit * 1.05) / (driveLimit * 0.9 + 1), 0, 1);
  state.wheelSpin += (spinTarget - state.wheelSpin) * Math.min(1, dt * SIM.spinRecovery);
  if (!input.handbrake && input.throttle < 0.05) state.wheelSpin *= 1 - Math.min(1, dt * 2.5);
  state.slip = clamp(Math.max(latSlip, state.wheelSpin * 0.85), 0, 1);
  state.odometer += newSpeed * dt;

  return result;
}

/**
 * Whether the surface under a world position is part of the racing surface.
 * Delegates to the track's painted road mask so gravel rally stages and dirt
 * desert circuits are judged exactly like a tarmac street circuit.
 */
function isOnRoad(track: BuiltTrack, x: number, y: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return isRoadAt(track, x, y);
}

function sampleSurface(track: BuiltTrack, x: number, y: number): SurfaceId {
  // Guard against NaN/Infinity leaking in from an unstable frame: without this
  // the cell lookup silently yields `undefined` and the surface table blows up.
  if (!Number.isFinite(x) || !Number.isFinite(y)) return 'wall';
  const cx = Math.floor(x / GRID_CELL);
  const cy = Math.floor(y / GRID_CELL);
  if (cx < 0 || cy < 0 || cx >= track.grid.cols || cy >= track.grid.rows) return 'wall';
  return SURFACE_LIST[track.grid.surface[cy * track.grid.cols + cx]!] ?? 'asphalt';
}

interface WallHit {
  hit: boolean;
  x: number;
  y: number;
  nx: number;
  ny: number;
  side: number;
}

/**
 * Circle-vs-grid collision test at three points along the car's spine. Returns
 * the clamped position plus a contact normal when the car has overlapped a
 * solid cell.
 */
function collideWalls(
  track: BuiltTrack,
  x: number,
  y: number,
  angle: number,
  prevX: number,
  prevY: number,
  halfLong: number,
  halfLat: number,
): WallHit {
  const cs = Math.cos(angle);
  const sn = Math.sin(angle);
  const pts: Array<[number, number]> = [
    [x + cs * halfLong * 0.8, y + sn * halfLong * 0.8],
    [x, y],
    [x - cs * halfLong * 0.8, y - sn * halfLong * 0.8],
  ];
  for (const [px, py] of pts) {
    if (isSolidAt(track, px, py) || cellNearSolid(track, px, py, halfLat)) {
      // Binary search back along the movement vector for the last free spot.
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 7; i++) {
        const mid = (lo + hi) / 2;
        const mx = prevX + (x - prevX) * mid;
        const my = prevY + (y - prevY) * mid;
        const free = !isSolidAt(track, mx, my);
        if (free && !cellNearSolid(track, mx, my, halfLat * 0.75)) lo = mid;
        else hi = mid;
      }
      const rx = prevX + (x - prevX) * lo;
      const ry = prevY + (y - prevY) * lo;
      const nrm = wallNormal(track, rx, ry, halfLat + 2);
      const offX = px - rx;
      const offY = py - ry;
      const side = Math.sign(offX * nrm.y - offY * nrm.x) || 1;
      return { hit: true, x: rx, y: ry, nx: nrm.x, ny: nrm.y, side };
    }
  }
  return { hit: false, x, y, nx: 0, ny: 0, side: 0 };
}

function wallNormal(track: BuiltTrack, x: number, y: number, radius: number): { x: number; y: number } {
  const g = track.grid;
  const r = Math.ceil(radius / g.cell);
  const cx = Math.floor(x / g.cell);
  const cy = Math.floor(y / g.cell);
  let nx = 0;
  let ny = 0;
  for (let oy = -r; oy <= r; oy++) {
    for (let ox = -r; ox <= r; ox++) {
      const gx = cx + ox;
      const gy = cy + oy;
      if (gx < 0 || gy < 0 || gx >= g.cols || gy >= g.rows) continue;
      if (g.solid[gy * g.cols + gx] !== 1) continue;
      const px = gx * g.cell + g.cell / 2;
      const py = gy * g.cell + g.cell / 2;
      const dx = x - px;
      const dy = y - py;
      const d = Math.hypot(dx, dy) || 1;
      nx += dx / d;
      ny += dy / d;
    }
  }
  const len = Math.hypot(nx, ny);
  if (len < 1e-6) return { x: 0, y: -1 };
  return { x: nx / len, y: ny / len };
}

/**
 * Simple circle-circle resolution between two cars. Applies equal and opposite
 * impulses so heavier cars shove lighter ones without launching anybody.
 */
export function resolveCarCollision(
  a: VehicleState,
  aMass: number,
  aRadius: number,
  b: VehicleState,
  bMass: number,
  bRadius: number,
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const minDist = aRadius + bRadius;
  const d2 = dx * dx + dy * dy;
  if (d2 >= minDist * minDist || d2 < 1e-6) return 0;
  const d = Math.sqrt(d2);
  const nx = dx / d;
  const ny = dy / d;
  const pen = minDist - d;
  const totalMass = aMass + bMass;
  const aShare = bMass / totalMass;
  const bShare = aMass / totalMass;
  a.x -= nx * pen * aShare;
  a.y -= ny * pen * aShare;
  b.x += nx * pen * bShare;
  b.y += ny * pen * bShare;

  const rvx = b.vx - a.vx;
  const rvy = b.vy - a.vy;
  const vn = rvx * nx + rvy * ny;
  if (vn > 0) return 0;
  const e = 0.35;
  const j = (-(1 + e) * vn) / (1 / aMass + 1 / bMass);
  const aImpX = -j * nx / aMass;
  const aImpY = -j * ny / aMass;
  const bImpX = j * nx / bMass;
  const bImpY = j * ny / bMass;
  a.vx += aImpX;
  a.vy += aImpY;
  b.vx += bImpX;
  b.vy += bImpY;

  // A bit of spin so contact feels physical rather than magnetic.
  a.yawRate -= ((aImpX * dy - aImpY * dx) / Math.max(400, aMass)) * 4;
  b.yawRate += ((bImpX * dy - bImpY * dx) / Math.max(400, bMass)) * 4;

  const impact = Math.abs(vn);
  if (impact > a.impact) {
    a.impact = impact;
    a.impactNx = nx;
    a.impactNy = ny;
  }
  if (impact > b.impact) {
    b.impact = impact;
    b.impactNx = -nx;
    b.impactNy = -ny;
  }
  return impact;
}

/** Places a car safely back on the racing surface facing the right way. */
export function respawnOnPath(
  state: VehicleState,
  track: BuiltTrack,
  pathIndex: number,
): void {
  const path = track.path;
  const p = path[((pathIndex % path.length) + path.length) % path.length]!;
  state.x = p.x;
  state.y = p.y;
  state.angle = p.angle;
  state.vx = 0;
  state.vy = 0;
  state.vLong = 0;
  state.vLat = 0;
  state.yawRate = 0;
  state.steerAngle = 0;
  state.stuckTime = 0;
}

